import {
  CoverageDetails,
  HasCoverageInput,
  HasEndorsementInput,
  HasEnterpriseRatingInput,
  HasScheduleRatingInput,
  HasSubCoverageInput,
  HasSubjectivityInput,
  QuoteDetails,
  SubmissionType,
  UpsertQuoteMutationVariables,
  getDisplayNameFromEntity,
  getPaginatedNodes,
  sortCoveragesByType,
} from '@converge/graphql';
import {
  IExcessQuoteLineItem,
  IQuoteLineItem,
  IQuoteLineItemGroup,
  QuoteLineItem,
  QuoteLineItemType,
  quoteLineItemTypeToLabel,
} from './quote-list.context';

import { COVERAGE_TYPE_LABELS } from '@converge/data';

export const combineQuoteLineItems = <
  LineItem extends QuoteLineItem = QuoteLineItem
>(
  policyType: SubmissionType,
  quotes: QuoteDetails[]
): LineItem[] => {
  if (policyType === SubmissionType.Excess) {
    const lineItems = quotes.reduce<IExcessQuoteLineItem[]>(
      (acc, quote, index) => {
        for (const layer of getPaginatedNodes(quote.excessTowerLayers)) {
          for (const participant of getPaginatedNodes(
            layer.participants ?? {
              edges: [],
            }
          )) {
            const uuid = [
              layer.position ?? 'N/A',
              participant.uuid ?? 'N/A',
            ].join(':');
            if (!acc.find((item) => item.uuid === uuid)) {
              acc.push({
                policyType: SubmissionType.Excess,
                uuid,
                attachmentPoint: participant.quote?.attachmentPoint,
                limit: participant.quote?.limit,
                name: getDisplayNameFromEntity(participant) ?? 'N/A',
                layer: layer.name ?? 'N/A',
                policyNumber: participant.quote?.policyReferenceId,
                boundPremium: participant.quote?.boundPremium,
                boundIncreasedLimitFactor:
                  participant.quote?.boundIncreasedLimitFactor,
                layerLimit: layer.limit,
                layerAttachmentPoint: layer.attachmentPoint,
                layerPremium: layer.boundPremium,
              });
            }
          }
        }
        return acc;
      },
      []
    ) as LineItem[];

    return lineItems;
  }

  const combinedCoverages = sortCoveragesByType(
    quotes.reduce<CoverageDetails[]>((coverages, quote) => {
      coverages.push(
        ...(getPaginatedNodes(quote.coverages).filter(
          (coverage) =>
            !coverages.find(
              (existingCoverage) => existingCoverage.uuid === coverage.uuid
            )
        ) as CoverageDetails[])
      );
      return coverages;
    }, [])
  );
  // .reduce<((CoverageDetails | RaterSubCoverageDetails) & { isSubCoverage: boolean })[]>((acc, coverage) => {
  //   return [
  //     ...acc,
  //     {
  //       ...coverage,
  //       isSubCoverage: false,
  //     },
  //     ...getPaginatedNodes(coverage.subCoverages).map((subCoverage) => ({
  //       ...subCoverage,
  //       isSubCoverage: true,
  //     })),
  //   ];
  // }, [])
  return [
    ...combinedCoverages.map(
      (lineItem): IQuoteLineItem => ({
        policyType,
        uuid: lineItem.uuid ?? 'N/A',
        name: lineItem.name ?? 'N/A',
        type: QuoteLineItemType.Coverage,
        included: lineItem.quote?.included === true,
        fusionScore:
          lineItem.quote && 'fusionScore' in lineItem.quote
            ? lineItem.quote?.fusionScore
            : undefined,
        limit:
          lineItem.quote && 'limit' in lineItem.quote
            ? lineItem.quote?.limit
            : undefined,
        retention:
          lineItem.quote && 'retention' in lineItem.quote
            ? lineItem.quote?.retention
            : undefined,
        modifiedCoveragePremium:
          lineItem.quote && 'modifiedCoveragePremium' in lineItem.quote
            ? lineItem.quote?.modifiedCoveragePremium
            : undefined,
        coverageType:
          'type' in lineItem && lineItem?.type
            ? COVERAGE_TYPE_LABELS[lineItem.type]
            : undefined,
        childItems: getPaginatedNodes(lineItem.subCoverages).map(
          (subCoverage): IQuoteLineItem => ({
            policyType,
            uuid: subCoverage.uuid ?? 'N/A',
            name: subCoverage.name ?? 'N/A',
            type: QuoteLineItemType.SubCoverage,
            included: subCoverage.quote?.included === true,
            limit: subCoverage.quote?.subLimit,
            retention: subCoverage.quote?.subRetention,
            parentCoverageUuid: lineItem.uuid,
          })
        ),
      })
    ),
    ...quotes.reduce<IQuoteLineItem[]>((lineItems, quote): IQuoteLineItem[] => {
      const { endorsements, subjectivities } = {
        endorsements: getPaginatedNodes(quote.endorsements),
        subjectivities: getPaginatedNodes(quote.subjectivities),
      };

      lineItems.push(
        ...endorsements
          .filter((lineItem) => {
            if (lineItem.name === null) {
              return false;
            }
            return !lineItems.find(
              (item) =>
                item.uuid === lineItem.uuid &&
                item.type === QuoteLineItemType.Endorsement
            );
          })
          .map(
            (lineItem): IQuoteLineItem => ({
              policyType,

              uuid: lineItem.uuid ?? 'n/a',
              name: lineItem.name ?? 'N/A',
              type: QuoteLineItemType.Endorsement,
              included: lineItem.quote?.included === true,
              fusionScore: lineItem.quote?.fusionScore,
              limit: lineItem.quote?.limit,
              retention: lineItem.quote?.retention,
              modifiedCoveragePremium: lineItem.quote?.modifiedCoveragePremium,
              nonPremiumBearing: lineItem?.nonPremiumBearing ?? false,
            })
          ),
        ...subjectivities
          .filter((lineItem) => {
            if (lineItem.name === null) {
              return false;
            }
            return !lineItems.find(
              (item) =>
                item.uuid === lineItem.uuid &&
                item.type === QuoteLineItemType.Subjectivity
            );
          })
          .map(
            (lineItem): IQuoteLineItem => ({
              policyType,
              uuid: lineItem.uuid ?? 'n/a',
              name: lineItem.name ?? 'N/A',
              type: QuoteLineItemType.Subjectivity,
              included: lineItem.quote?.included === true,
            })
          )
      );
      return lineItems;
    }, []),
  ] as LineItem[];
};

export const combineQuoteLineItemsIntoGroups = <
  LineItem extends QuoteLineItem = QuoteLineItem
>(
  type: SubmissionType,
  quotes: QuoteDetails[]
): IQuoteLineItemGroup<LineItem>[] => {
  const lineItems = combineQuoteLineItems<LineItem>(type, quotes);

  const groups = lineItems.reduce<IQuoteLineItemGroup<LineItem>[]>(
    (groups, lineItem) => {
      const groupId =
        lineItem.policyType === SubmissionType.Primary
          ? lineItem.coverageType ?? lineItem.type
          : lineItem.layer;

      const group = groups.find((group) => group.id === groupId);

      const layerLimit =
        'layerLimit' in lineItem ? lineItem.layerLimit ?? null : null;
      const layerPremium =
        'layerPremium' in lineItem ? lineItem.layerPremium ?? null : null;
      const layerAttachmentPoint =
        'layerAttachmentPoint' in lineItem
          ? lineItem.layerAttachmentPoint ?? null
          : null;

      if (group) {
        group.layerLimits = [...group.layerLimits, layerLimit];
        group.layerPremiums = [...group.layerPremiums, layerPremium];
        group.layerAttachmentPoints = [
          ...group.layerAttachmentPoints,
          layerAttachmentPoint,
        ];
        group.items.push(lineItem);
      } else {
        const layerLimits = [layerLimit];
        const layerPremiums = [layerPremium];
        const layerAttachmentPoints = [layerAttachmentPoint];

        groups.push({
          id: groupId,
          name:
            lineItem.policyType === SubmissionType.Primary
              ? lineItem.coverageType ?? quoteLineItemTypeToLabel(lineItem.type)
              : lineItem.layer,
          items: [lineItem],
          layerLimits,
          layerPremiums,
          layerAttachmentPoints,
        });
      }

      return groups;
    },
    []
  );

  return groups;
};

export const flattenQuoteLineItems = <
  LineItem extends QuoteLineItem = QuoteLineItem
>(
  type: SubmissionType,
  quoteLineItemsInput: LineItem[]
): LineItem[] => {
  const quoteLineItems = (
    type === SubmissionType.Primary
      ? (quoteLineItemsInput as IQuoteLineItem[])
      : (quoteLineItemsInput as IExcessQuoteLineItem[])
  ) as LineItem[];

  return quoteLineItems.reduce<LineItem[]>((acc, lineItem) => {
    if (lineItem.policyType === SubmissionType.Excess) {
      return [...acc, lineItem];
    }

    if (lineItem.childItems && lineItem.childItems.length > 0) {
      return [
        ...acc,
        lineItem,
        ...flattenQuoteLineItems<LineItem>(
          type,
          lineItem.childItems as LineItem[]
        ).map((childItem) => ({
          ...childItem,
          isChildItem: true,
        })),
      ];
    }

    return [...acc, lineItem];
  }, []);
};

export interface IUnderwritingQuoteToUpsertVariablesInput {
  submissionUuid: string; // submission.uuid
  quote: QuoteDetails;
}

export const underwritingQuoteToUpsertVariables = ({
  quote,
  submissionUuid,
}: IUnderwritingQuoteToUpsertVariablesInput): UpsertQuoteMutationVariables => {
  return {
    excessTowerLayers: [],
    enterpriseRatings: quote.enterpriseRatings.edges.map(
      ({ node: enterpriseRating }): HasEnterpriseRatingInput => {
        return {
          enterpriseRatingUuid: enterpriseRating.uuid,
          uuid: enterpriseRating.quote?.uuid as string,
          riskLevel: enterpriseRating.quote?.riskLevel,
        };
      }
    ),
    scheduleRatings: quote.scheduleRatings.edges.map(
      ({ node: scheduleRating }): HasScheduleRatingInput => {
        return {
          scheduleRatingUuid: scheduleRating.uuid,
          uuid: scheduleRating.quote?.uuid as string,
          percentage: scheduleRating.quote?.percentage,
        };
      }
    ),
    subjectivities:
      quote.subjectivities && quote.subjectivities.edges[0]?.node.uuid
        ? quote.subjectivities.edges.map(
            ({ node: subjectivity }): HasSubjectivityInput => {
              return {
                subjectivityUuid: subjectivity.uuid as string,
                included: subjectivity?.quote?.included ?? false,
              };
            }
          )
        : [],
    coverages: quote.coverages.edges.map(
      ({ node: coverage }): HasCoverageInput => {
        return {
          fullPriorActs: coverage.quote?.fullPriorActs,
          basePremium: coverage.quote?.basePremium,
          fusionScore: coverage.quote?.fusionScore,
          fusionScoreModifier: coverage.quote?.fusionScoreModifier,
          coverageUuid: coverage.uuid,
          hazardGroup: coverage.quote?.hazardGroup,
          included: coverage.quote?.included ?? false,
          limit: coverage.quote?.limit,
          modifiedCoveragePremium: coverage.quote?.modifiedCoveragePremium,
          // TODO: use actual notes here
          notes: '',
          ratedFusionScoreModifier: coverage.quote?.ratedFusionScoreModifier,
          selectedFusionScoreModifier:
            coverage.quote?.selectedFusionScoreModifier,
          restorationPeriodDays: coverage.quote?.restorationPeriodDays,
          retention: coverage.quote?.retention,
          technicalPremium: coverage.quote?.technicalPremium,
          uuid: coverage.uuid,
          waitingPeriodHours: coverage.quote?.waitingPeriodHours,
          commissionAdjustmentFactor:
            coverage.quote?.commissionAdjustmentFactor,
          enhancedMediaFactor: coverage.quote?.enhancedMediaFactor,
          limitRetentionFactor: coverage.quote?.limitRetentionFactor,
          policyTermFactor: coverage.quote?.policyTermFactor,
          priorActsFactor: coverage.quote?.priorActsFactor,
          restorationPeriodFactor: coverage?.quote?.restorationPeriodFactor,
          sharedCoverageAggregateFactor:
            coverage.quote?.sharedCoverageAggregateFactor,
          sharedPolicyAggregateFactor:
            coverage.quote?.sharedPolicyAggregateFactor,
          subCoverageFactor: coverage.quote?.subCoverageFactor,
          waitingPeriodFactor: coverage.quote?.waitingPeriodFactor,
          maxFusionScoreModifier: coverage.quote?.maxFusionScoreModifier,
          minFusionScoreModifier: coverage.quote?.minFusionScoreModifier,
          retroactiveDate: coverage.quote?.retroactiveDate,
          subCoverages: coverage.subCoverages.edges.map(
            (subCoverage): HasSubCoverageInput => {
              return {
                coverageUuid: coverage.uuid,
                uuid: subCoverage.node.quote?.uuid,
                included: subCoverage.node.quote?.included ?? false,
                subLimit: subCoverage.node.quote?.subLimit,
                subRetention: subCoverage.node.quote?.subRetention,
                subCoverageUuid: subCoverage.node.uuid,
              };
            }
          ),
        };
      }
    ),
    endorsements:
      quote.endorsements.edges && quote.endorsements.edges[0].node.uuid
        ? quote?.endorsements?.edges.map(
            ({ node: { uuid, quote } }): HasEndorsementInput => {
              return {
                fullPriorActs: quote?.fullPriorActs,
                included: quote?.included ?? false,
                notes: '',
                limit: quote?.limit,
                retention: quote?.retention,
                endorsementUuid: uuid as string,
                basePremium: quote?.basePremium,
                fusionScore: quote?.fusionScore,
                fusionScoreModifier: quote?.fusionScoreModifier,
                hazardGroup: quote?.hazardGroup,
                modifiedCoveragePremium: quote?.modifiedCoveragePremium,
                restorationPeriodDays: quote?.restorationPeriodDays,
                technicalPremium: quote?.technicalPremium,
                waitingPeriodHours: quote?.waitingPeriodHours,
                commissionAdjustmentFactor: quote?.commissionAdjustmentFactor,
                enhancedMediaFactor: quote?.enhancedMediaFactor,
                limitRetentionFactor: quote?.limitRetentionFactor,
                policyTermFactor: quote?.policyTermFactor,
                priorActsFactor: quote?.priorActsFactor,
                restorationPeriodFactor: quote?.restorationPeriodFactor,
                sharedCoverageAggregateFactor:
                  quote?.sharedCoverageAggregateFactor,
                sharedPolicyAggregateFactor: quote?.sharedPolicyAggregateFactor,
                subCoverageFactor: quote?.subCoverageFactor,
                waitingPeriodFactor: quote?.waitingPeriodFactor,
                maxFusionScoreModifier: quote?.maxFusionScoreModifier,
                minFusionScoreModifier: quote?.minFusionScoreModifier,
                retroactiveDate: quote?.retroactiveDate,
                ratedFusionScoreModifier: quote?.ratedFusionScoreModifier,
                selectedFusionScoreModifier: quote?.selectedFusionScoreModifier,
              };
            }
          )
        : [],
    details: {
      name: quote.name,
      type: quote.type,
      uuid: quote.uuid,
      referenceId: quote.referenceId,
      quotedPremium: quote.quotedPremium,
      ratedPremium: quote.ratedPremium,
      limit: quote.limit,
      modifiedCoveragePremium: quote.modifiedCoveragePremium,
      order: quote.order,
      retention: quote.retention,
      technicalPremium: quote.technicalPremium,
      endorsementImpact: quote.endorsementImpact,
      status: quote.status,
    },
    submissionUuid,
  };
};
